import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Leitura do indicador da balança rodoviária via Web Serial API (Chrome/Edge, HTTPS).
 * Perfis prontos (Toledo Prix/9091 etc.) ou genérico; opcionalmente faz POLLING (envia ENQ e lê a resposta).
 * Config em localStorage. Estável = últimas N leituras dentro da tolerância (ou flag de estável do quadro).
 * Autodetecção: testa baud/bits/paridade até os bytes chegarem legíveis (dígitos) e ajusta perfil/config.
 */
const CFG_KEY = "chemwms_balanca_serial_cfg";

export const PERFIS = {
  GENERICO: {
    nome: "Genérico — primeiro número do quadro",
    regex: "([+-]?\\d+(?:[.,]\\d+)?)", fator: 1, poll: "", pollMs: 0, delim: "\\r|\\n|\\x03",
    dica: "Serve para a maioria dos indicadores que transmitem continuamente algo como '  15420 kg'.",
  },
  TOLEDO_P05: {
    nome: "Toledo 9091 / Prix — contínuo P05 (STX + 3 status + 6 peso + 6 tara)",
    regex: "\\x02[\\s\\S]{3}(\\d{6})\\d{6}", fator: 1, poll: "", pollMs: 0, delim: "\\r|\\n",
    dica: "Protocolo contínuo padrão Toledo (P05). Configure o indicador para transmissão contínua (Prt1/P05), 9600 8N1.",
  },
  TOLEDO_PRT2: {
    nome: "Toledo Prix — contínuo Prt2 (STX + peso + ETX)",
    regex: "\\x02\\s*([+-]?\\d{5,7})", fator: 1, poll: "", pollMs: 0, delim: "\\x03|\\r|\\n",
    dica: "Indicador em Prt2 com transmissão contínua (parâmetro C16 = dL). Peso sem ponto decimal — ajuste o fator se vier em gramas.",
  },
  TOLEDO_PRT3: {
    nome: "Toledo Prix — Prt3 por consulta (envia ENQ, recebe STX + peso + ETX)",
    regex: "\\x02\\s*([+-]?\\d{5,7})", fator: 1, poll: "\\x05", pollMs: 500, delim: "\\x03|\\r|\\n",
    dica: "O sistema envia ENQ (05h) a cada 500 ms e o indicador responde com o peso. 'IIIII' = instável, 'SSSSS' = sobrecarga, 'NNNNN' = negativo.",
  },
};

export const CFG_PADRAO = {
  perfil: "GENERICO",
  baudRate: 9600, dataBits: 8, stopBits: 1, parity: "none",
  regex: PERFIS.GENERICO.regex, fator: 1, poll: "", pollMs: 0, delim: PERFIS.GENERICO.delim,
  estavelN: 5, tolerancia: 10,
  autoConectar: true,
};

// Combinações testadas pela autodetecção, na ordem mais provável para indicadores Toledo/Prix e genéricos
export const COMBOS_DETECCAO = [
  [9600, 8, "none"], [4800, 8, "none"], [2400, 8, "none"], [19200, 8, "none"],
  [9600, 7, "even"], [4800, 7, "even"], [2400, 7, "even"],
  [9600, 8, "even"], [4800, 8, "even"], [9600, 7, "odd"], [4800, 7, "odd"],
  [1200, 8, "none"], [38400, 8, "none"],
];

export const carregarCfg = () => {
  try { return { ...CFG_PADRAO, ...(JSON.parse(localStorage.getItem(CFG_KEY) || "{}")) }; } catch { return { ...CFG_PADRAO }; }
};
export const salvarCfg = (cfg) => localStorage.setItem(CFG_KEY, JSON.stringify(cfg));
export const serialSuportado = () => typeof navigator !== "undefined" && !!navigator.serial;

const unescape = (s) => String(s || "").replace(/\\x([0-9a-fA-F]{2})/g, (_, h) => String.fromCharCode(parseInt(h, 16)));
const visivel = (s) => s.replace(/[\x00-\x1f\x7f]/g, (c) => `<${c.charCodeAt(0).toString(16).padStart(2, "0")}>`);
const esperar = (ms) => new Promise((r) => setTimeout(r, ms));
const latin1 = new TextDecoder("latin1");

// Byte "legível": ASCII imprimível ou controle esperado do protocolo (STX, ETX, ENQ, CR, LF, TAB)
const legivel = (b) => (b >= 0x20 && b <= 0x7e) || b === 0x02 || b === 0x03 || b === 0x05 || b === 0x0d || b === 0x0a || b === 0x09;

/** Avalia bytes recebidos: % legível e se há dígitos em sequência (peso). Sugere perfil pelo formato do quadro. */
export const avaliarBytes = (bytes, comPoll = false) => {
  const total = bytes.length;
  if (!total) return { total, legivel: 0, temPeso: false, perfil: null };
  const nLeg = bytes.reduce((acc, b) => acc + (legivel(b) ? 1 : 0), 0);
  const texto = latin1.decode(Uint8Array.from(bytes));
  const temPeso = /\d{3,}/.test(texto);
  let perfil = null;
  if (temPeso && nLeg / total >= 0.9) {
    if (/\x02[\s\S]{3}\d{6}\d{6}/.test(texto)) perfil = "TOLEDO_P05";
    else if (/\x02\s*[+-]?\d{5,7}/.test(texto)) perfil = comPoll ? "TOLEDO_PRT3" : "TOLEDO_PRT2";
    else perfil = "GENERICO";
  }
  return { total, legivel: nLeg / total, temPeso, perfil };
};

const opcoesPorta = (baudRate, dataBits, stopBits, parity) => {
  // Igual ao que funciona no Chrome/Windows: só baudRate; demais parâmetros apenas se diferentes do padrão 8N1
  const opts = { baudRate: Number(baudRate) || 9600 };
  if (Number(dataBits) && Number(dataBits) !== 8) opts.dataBits = Number(dataBits);
  if (Number(stopBits) && Number(stopBits) !== 1) opts.stopBits = Number(stopBits);
  if (parity && parity !== "none") opts.parity = parity;
  return opts;
};

// O driver (FTDI/Prolific) demora a liberar o handle após fechar: tenta algumas vezes com espera crescente
const abrirComRetry = async (port, opts) => {
  let ultimo = null;
  for (let tentativa = 1; tentativa <= 4; tentativa++) {
    try { await port.open(opts); return; } catch (e) {
      ultimo = e;
      if (/already open/i.test(String(e?.message || e))) { try { await port.close(); } catch { /* ignore */ } }
      await esperar(400 * tentativa);
    }
  }
  throw ultimo;
};

/** Escuta a porta por `ms` e devolve os bytes recebidos; se `pollBytes` for informado, envia a consulta a cada 300 ms. */
const escutar = async (port, ms, pollBytes) => {
  const bytes = [];
  const fim = Date.now() + ms;
  let timer = null;
  if (pollBytes && port.writable) {
    const enviar = async () => { try { const w = port.writable.getWriter(); await w.write(pollBytes); w.releaseLock(); } catch { /* ignore */ } };
    enviar(); timer = setInterval(enviar, 300);
  }
  while (Date.now() < fim && port.readable) {
    const reader = port.readable.getReader();
    const timeout = setTimeout(() => reader.cancel().catch(() => {}), Math.max(50, fim - Date.now()));
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        if (value) bytes.push(...value);
      }
    } catch { /* erro de framing/paridade: segue lendo */ } finally {
      clearTimeout(timeout);
      try { reader.releaseLock(); } catch { /* ignore */ }
    }
  }
  if (timer) clearInterval(timer);
  return bytes;
};

export function useBalancaSerial() {
  const [cfg, setCfg] = useState(carregarCfg);
  const cfgRef = useRef(cfg);
  const [estado, setEstado] = useState("DESCONECTADO"); // DESCONECTADO | CONECTANDO | CONECTADO | ERRO
  const [erro, setErro] = useState("");
  const [aviso, setAviso] = useState("");
  const [peso, setPeso] = useState(null);
  const [estavel, setEstavel] = useState(false);
  const [bruto, setBruto] = useState([]);
  const [ultimaLeitura, setUltimaLeitura] = useState(null);
  const [detectando, setDetectando] = useState(null); // texto de progresso da autodetecção
  const [ilegivel, setIlegivel] = useState(false);     // bytes chegando mas sem texto legível (baud/paridade errados)
  const portRef = useRef(null);
  const readerRef = useRef(null);
  const pollRef = useRef(null);
  const historicoRef = useRef([]);
  const vivoRef = useRef(false);
  const qualidadeRef = useRef({ total: 0, leg: 0 });

  const atualizarCfg = useCallback((novo) => {
    setCfg((c) => {
      let n = { ...c, ...novo };
      if (novo.perfil && PERFIS[novo.perfil]) {
        const p = PERFIS[novo.perfil];
        n = { ...n, regex: p.regex, fator: p.fator, poll: p.poll, pollMs: p.pollMs, delim: p.delim };
      }
      cfgRef.current = n; salvarCfg(n); return n;
    });
  }, []);

  const processarQuadro = useCallback((quadro) => {
    const c = cfgRef.current;
    if (!quadro || !quadro.trim()) return;
    setBruto((b) => [`${new Date().toLocaleTimeString("pt-BR")}  ${visivel(quadro)}`, ...b].slice(0, 12));
    if (/I{4,}/.test(quadro)) { setEstavel(false); setAviso("Indicador: peso INSTÁVEL"); return; }
    if (/S{4,}/.test(quadro)) { setEstavel(false); setAviso("Indicador: SOBRECARGA"); return; }
    if (/N{4,}/.test(quadro)) { setEstavel(false); setAviso("Indicador: peso NEGATIVO"); return; }
    let m = null;
    try { m = new RegExp(unescape(c.regex)).exec(quadro); } catch { m = /([+-]?\d+(?:[.,]\d+)?)/.exec(quadro); }
    if (!m) return;
    const kg = Math.round(parseFloat(String(m[1]).replace(",", ".")) * (Number(c.fator) || 1));
    if (!Number.isFinite(kg)) return;
    setAviso("");
    const n = Math.max(2, Number(c.estavelN) || 5);
    historicoRef.current = [kg, ...historicoRef.current].slice(0, n);
    const h = historicoRef.current;
    setPeso(kg); setEstavel(h.length >= n && Math.max(...h) - Math.min(...h) <= (Number(c.tolerancia) || 0)); setUltimaLeitura(Date.now());
  }, []);

  // Qualidade dos bytes recebidos: muitos bytes e menos de 60% legíveis → baud/paridade provavelmente errados
  const avaliarQualidade = useCallback((value) => {
    const q = qualidadeRef.current;
    for (const b of value) { q.total += 1; if (legivel(b)) q.leg += 1; }
    if (q.total > 400) { q.total = Math.round(q.total / 2); q.leg = Math.round(q.leg / 2); } // janela deslizante
    setIlegivel(q.total >= 60 && q.leg / q.total < 0.6);
  }, []);

  const pararPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const desconectar = useCallback(async () => {
    vivoRef.current = false; pararPoll();
    try { await readerRef.current?.cancel(); } catch { /* ignore */ }
    readerRef.current = null;
    const port = portRef.current; portRef.current = null;
    if (port) { try { await port.close(); } catch { /* já fechada / travada */ } }
    setEstado("DESCONECTADO"); setEstavel(false); setIlegivel(false);
  }, []);

  const lerLoop = useCallback(async (port) => {
    const decoder = new TextDecoder("latin1");
    let buffer = "";
    while (vivoRef.current && port.readable) {
      const reader = port.readable.getReader();
      readerRef.current = reader;
      try {
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          avaliarQualidade(value);
          buffer += decoder.decode(value, { stream: true });
          const delim = new RegExp(unescape(cfgRef.current.delim || "\\r|\\n"));
          let m;
          while ((m = delim.exec(buffer)) && m.index >= 0) {
            processarQuadro(buffer.slice(0, m.index));
            buffer = buffer.slice(m.index + m[0].length);
          }
          if (buffer.length > 128) { processarQuadro(buffer); buffer = ""; }
        }
      } catch (e) {
        // Erro NÃO fatal (framing/paridade/overrun): o navegador recria port.readable → continua lendo.
        // Erro fatal (cabo removido): port.readable fica null → sai do laço.
        if (vivoRef.current && !port.readable) { setErro(`Conexão perdida: ${e?.message || e}`); setEstado("ERRO"); }
      } finally {
        try { reader.releaseLock(); } catch { /* ignore */ }
      }
      if (vivoRef.current && port.readable) await esperar(50);
    }
    if (vivoRef.current) { vivoRef.current = false; pararPoll(); portRef.current = null; try { await port.close(); } catch { /* ignore */ } setEstado((s) => (s === "ERRO" ? s : "DESCONECTADO")); }
  }, [processarQuadro, avaliarQualidade]);

  const iniciarPoll = useCallback((port) => {
    pararPoll();
    const c = cfgRef.current;
    const cmd = unescape(c.poll || "");
    if (!cmd || !(Number(c.pollMs) > 0) || !port.writable) return;
    const bytes = new Uint8Array([...cmd].map((ch) => ch.charCodeAt(0)));
    pollRef.current = setInterval(async () => {
      if (!vivoRef.current || !port.writable) return;
      try { const w = port.writable.getWriter(); await w.write(bytes); w.releaseLock(); } catch { /* ignore */ }
    }, Number(c.pollMs));
  }, []);

  const abrindoRef = useRef(false);

  const abrir = useCallback(async (port) => {
    const c = cfgRef.current;
    await abrirComRetry(port, opcoesPorta(c.baudRate, c.dataBits, c.stopBits, c.parity));
    portRef.current = port; vivoRef.current = true; historicoRef.current = []; qualidadeRef.current = { total: 0, leg: 0 };
    setErro(""); setIlegivel(false); setEstado("CONECTADO");
    lerLoop(port);
    iniciarPoll(port);
  }, [lerLoop, iniciarPoll]);

  const mensagemErro = (e) => {
    const msg = String(e?.message || e);
    if (e?.name === "NotFoundError") return "Nenhuma porta selecionada.";
    if (/Failed to open/i.test(msg)) {
      return "Não foi possível abrir a porta COM. Normalmente ela está EM USO por outro programa ou outra aba do navegador "
        + "(feche o software antigo da balança / a página de teste LibraFlow e qualquer outra aba do ChemWMS com a Balança aberta), "
        + "ou o adaptador USB foi reconectado. Feche o outro programa, aguarde 2 segundos e clique em Conectar novamente.";
    }
    if (/access denied|permission/i.test(msg)) return "Acesso à porta negado pelo sistema operacional. Feche outros programas que usam a porta e tente de novo.";
    return msg;
  };

  const conectar = useCallback(async () => {
    if (!serialSuportado()) { setErro("Este navegador não suporta leitura serial. Use Chrome ou Edge."); setEstado("ERRO"); return; }
    if (abrindoRef.current) return;  // já há uma abertura em andamento (ex.: reconexão automática)
    abrindoRef.current = true;
    try {
      await desconectar();
      await esperar(300);  // dá tempo do driver liberar o handle
      setErro(""); setEstado("CONECTANDO");
      const port = await navigator.serial.requestPort();
      await abrir(port);
    } catch (e) {
      setErro(mensagemErro(e));
      setEstado("DESCONECTADO");
    } finally {
      abrindoRef.current = false;
    }
  }, [abrir, desconectar]);

  /**
   * AUTODETECÇÃO: fecha a porta atual (ou pede uma), testa cada combinação baud/bits/paridade por ~1,5 s
   * (primeiro escutando — modo contínuo; se nada chegar, enviando ENQ — modo consulta). A primeira que trouxer
   * texto legível com dígitos vira a configuração salva (com o perfil sugerido) e a porta é reaberta normalmente.
   */
  const detectar = useCallback(async () => {
    if (!serialSuportado()) { setErro("Este navegador não suporta leitura serial. Use Chrome ou Edge."); setEstado("ERRO"); return null; }
    if (abrindoRef.current || detectando) return null;
    abrindoRef.current = true;
    let port = portRef.current;
    let achou = null;
    try {
      await desconectar();
      await esperar(300);
      setErro(""); setAviso("");
      if (!port) port = await navigator.serial.requestPort();
      setEstado("CONECTANDO");
      const enq = new Uint8Array([0x05]);
      let erroAbrir = "";
      for (let i = 0; i < COMBOS_DETECCAO.length && !achou; i++) {
        const [baud, bits, par] = COMBOS_DETECCAO[i];
        const rotulo = `${baud} ${bits}${par[0].toUpperCase()}1`;
        setDetectando(`Testando ${rotulo} (${i + 1}/${COMBOS_DETECCAO.length})…`);
        try { await abrirComRetry(port, opcoesPorta(baud, bits, 1, par)); } catch (e) { erroAbrir = `Falha ao abrir em ${rotulo}: ${mensagemErro(e)}`; break; }
        try {
          let bytes = await escutar(port, 1500, null);
          let comPoll = false;
          if (bytes.length < 4) { bytes = await escutar(port, 900, enq); comPoll = true; }
          const r = avaliarBytes(bytes, comPoll);
          setBruto((b) => [`${new Date().toLocaleTimeString("pt-BR")}  [${rotulo}${comPoll ? " +ENQ" : ""}] ${r.total} bytes, ${Math.round(r.legivel * 100)}% legível  ${visivel(latin1.decode(Uint8Array.from(bytes.slice(0, 60))))}`, ...b].slice(0, 12));
          if (r.perfil) achou = { baud, bits, par, perfil: r.perfil, rotulo };
        } finally {
          try { await port.close(); } catch { /* ignore */ }
          await esperar(250);
        }
      }
      if (achou) {
        const p = PERFIS[achou.perfil];
        const n = { ...cfgRef.current, baudRate: achou.baud, dataBits: achou.bits, stopBits: 1, parity: achou.par,
          perfil: achou.perfil, regex: p.regex, fator: p.fator, poll: p.poll, pollMs: p.pollMs, delim: p.delim };
        cfgRef.current = n; salvarCfg(n); setCfg(n);
        setAviso(`Configuração detectada: ${achou.rotulo} · perfil ${p.nome}`);
        setDetectando(null);
        await abrir(port);
      } else {
        setDetectando(null);
        setEstado("DESCONECTADO");
        setErro(erroAbrir || "Nenhuma combinação trouxe peso legível. Verifique no menu do indicador se a transmissão serial está ativa (contínua ou por consulta) e a velocidade configurada.");
      }
    } catch (e) {
      setDetectando(null); setErro(mensagemErro(e)); setEstado("DESCONECTADO");
    } finally {
      abrindoRef.current = false;
    }
    return achou;
  }, [abrir, desconectar, detectando]);

  // Reconexão automática: porta já autorizada antes (sem abrir o seletor)
  useEffect(() => {
    if (!serialSuportado() || !cfgRef.current.autoConectar) return;
    let cancel = false;
    navigator.serial.getPorts().then(async (ports) => {
      if (cancel || ports.length !== 1 || portRef.current || abrindoRef.current) return;
      abrindoRef.current = true;
      setEstado("CONECTANDO");
      try { await abrir(ports[0]); } catch (e) { setErro(`Reconexão automática falhou — ${mensagemErro(e)}`); setEstado("DESCONECTADO"); }
      finally { abrindoRef.current = false; }
    }).catch(() => {});
    const onDisc = (ev) => { if (ev.target === portRef.current) { desconectar(); setErro("Indicador desconectado (cabo removido)."); } };
    navigator.serial.addEventListener("disconnect", onDisc);
    return () => { cancel = true; navigator.serial.removeEventListener("disconnect", onDisc); };
  }, [abrir, desconectar]);

  // Sem leitura há 5s → não considera estável
  useEffect(() => {
    const t = setInterval(() => { if (ultimaLeitura && Date.now() - ultimaLeitura > 5000) setEstavel(false); }, 1000);
    return () => clearInterval(t);
  }, [ultimaLeitura]);

  useEffect(() => () => { vivoRef.current = false; pararPoll(); portRef.current?.close?.().catch(() => {}); }, []);

  // Simulação (sem cabo): injeta N leituras de um peso, ou um quadro bruto exatamente como o indicador enviaria
  const simular = useCallback((kg) => { for (let i = 0; i < (Number(cfgRef.current.estavelN) || 5); i++) processarQuadro(`SIM ${kg} kg`); }, [processarQuadro]);
  const injetarQuadro = useCallback((texto) => processarQuadro(unescape(texto)), [processarQuadro]);

  return { cfg, atualizarCfg, estado, erro, aviso, peso, estavel, bruto, conectar, desconectar, detectar, detectando, ilegivel,
    simular, injetarQuadro, suportado: serialSuportado(), ultimaLeitura };
}
